import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LANGUAGE_MAPPINGS, NATIVE_LANGUAGES, I_SPEAK_TEXT } from '../settings/language-mapping';

@Component({
  selector: 'app-beginning',
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, TranslateModule],
  templateUrl: './beginning.page.html',
  styleUrls: ['./beginning.page.scss'],
})
export class BeginningPage implements OnInit {
  nativeLanguages = NATIVE_LANGUAGES;
  selectedNativeLanguage = 'en'; // Will be updated in ngOnInit
  targetLanguages: { code: string, name: string, flag: string }[] = [];
  selectedTargetLanguage = '';
  iSpeakText = I_SPEAK_TEXT;
  isLanguageInitialized = false; // Add flag to track initialization

  // Language flags mapping using PNG/SVG images
  languageFlags: { [key: string]: string } = {
    'en': 'assets/images/flags/us.svg',
    'es': 'assets/images/flags/es.svg',
    'fr': 'assets/images/flags/fr.svg',
    'de': 'assets/images/flags/de.svg',
    'it': 'assets/images/flags/it.svg',
    'pt': 'assets/images/flags/br.svg',
    'ru': 'assets/images/flags/ru.svg',
    'zh': 'assets/images/flags/cn.svg',
    'ja': 'assets/images/flags/jp.svg',
    'ko': 'assets/images/flags/kr.svg',
    'ar': 'assets/images/flags/sa.svg',
    'hi': 'assets/images/flags/in.svg',
    'th': 'assets/images/flags/th.svg',
    'vi': 'assets/images/flags/vn.svg',
    'tr': 'assets/images/flags/tr.svg',
    'pl': 'assets/images/flags/pl.svg',
    'nl': 'assets/images/flags/nl.svg',
    'sv': 'assets/images/flags/se.svg',
    'he': 'assets/images/flags/il.svg'
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    document.title = 'Choose Languages';
    
    // Initialize language detection synchronously
    this.initializeLanguage();
    
    // Test safe area values for debugging
    this.logSafeAreaValues();
  }

  private logSafeAreaValues() {
    // Log safe area values for debugging
    const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--ion-safe-area-top');
    const safeAreaBottom = getComputedStyle(document.documentElement).getPropertyValue('--ion-safe-area-bottom');
    
    console.log('[BeginningPage] Safe area values:', {
      top: safeAreaTop,
      bottom: safeAreaBottom,
      userAgent: navigator.userAgent,
      platform: navigator.platform
    });
  }

  private initializeLanguage() {
    // Get current language from TranslateService or detect browser language
    const currentLang = this.translate.currentLang || this.translate.getDefaultLang() || this.getBrowserLanguage();
    
    // Set the initial native language based on current translate service language
    const supportedLang = NATIVE_LANGUAGES.find(lang => lang.code === currentLang);
    this.selectedNativeLanguage = supportedLang ? currentLang : 'en';
    
    // Check for lang query parameter (this can override the detected language)
    this.route.queryParams.subscribe(params => {
      const langParam = params['lang'];
      if (langParam && typeof langParam === 'string') {
        // Check if the provided language code is supported
        const paramSupportedLang = NATIVE_LANGUAGES.find(lang => lang.code === langParam.toLowerCase());
        if (paramSupportedLang) {
          this.selectedNativeLanguage = langParam.toLowerCase();
        }
      }
      
      // Set the language first, then mark as initialized and update target languages
      this.translate.use(this.selectedNativeLanguage).subscribe(() => {
        this.isLanguageInitialized = true;
        this.updateTargetLanguages(); // Call updateTargetLanguages directly instead of onNativeLanguageChange
      });
    });
  }

  private getBrowserLanguage(): string {
    // Get browser language and extract language code
    const browserLang = navigator.language || (navigator as any).languages?.[0] || 'en';
    return browserLang.split('-')[0].toLowerCase();
  }

  onNativeLanguageChange() {
    // Update the app language and translate service native language
    this.translate.use(this.selectedNativeLanguage).subscribe(() => {
      this.isLanguageInitialized = true;
      this.updateTargetLanguages();
    });
  }

  private updateTargetLanguages() {
    // Get available target languages for selected native language and filter out the native language
    const availableLanguages = LANGUAGE_MAPPINGS[this.selectedNativeLanguage] || [];
    this.targetLanguages = availableLanguages
      .filter(lang => lang.code !== this.selectedNativeLanguage)
      .map(lang => ({
        ...lang,
        flag: this.languageFlags[lang.code] || '🌐'
      }));
    
    // Reset target language selection
    this.selectedTargetLanguage = '';
  }

  selectTargetLanguage(languageCode: string) {
    this.selectedTargetLanguage = languageCode;
  }

  goToLevel() {
    if (this.selectedTargetLanguage) {
      this.router.navigate(['/level'], {
        queryParams: {
          native: this.selectedNativeLanguage,
          target: this.selectedTargetLanguage
        }
      });
    }
  }

  getNativeSpeakText(): string {
    return this.iSpeakText[this.selectedNativeLanguage as keyof typeof I_SPEAK_TEXT] || '';
  }

  getSpeakTextForLanguage(langCode: string): string {
    return this.iSpeakText[langCode as keyof typeof I_SPEAK_TEXT] || langCode;
  }

  getMainHeadlineText(): string {
    // Don't show text until language is properly initialized
    if (!this.isLanguageInitialized) {
      return '';
    }

    if (this.selectedTargetLanguage) {
      // Try to get the specific "I_WANT_TO_LEARN_" + language code translation
      const specificKey = 'BEGINNING.I_WANT_TO_LEARN_' + this.selectedTargetLanguage.toUpperCase();
      const translation = this.translate.instant(specificKey);
      
      // If the specific translation exists and is not the key itself, use it
      if (translation && translation !== specificKey) {
        return translation;
      }
      
      // Fallback to generic "I want to learn" + language name
      const targetLang = this.targetLanguages.find(lang => lang.code === this.selectedTargetLanguage);
      if (targetLang) {
        const genericText = this.translate.instant('BEGINNING.I_WANT_TO_LEARN');
        return `${genericText} ${targetLang.name}`;
      }
    }
    
    // Default fallback
    return this.translate.instant('BEGINNING.I_WANT_TO_LEARN');
  }

  goBack() {
    this.router.navigate(['/login']);
  }

  onImageError(event: any, langCode: string) {
    // Fallback to a default flag image or emoji
    const fallbackEmojis: { [key: string]: string } = {
      'en': '🇺🇸', 'es': '🇪🇸', 'fr': '🇫🇷', 'de': '🇩🇪', 'it': '🇮🇹',
      'pt': '🇧🇷', 'ru': '🇷🇺', 'zh': '🇨🇳', 'ja': '🇯🇵', 'ko': '🇰🇷',
      'ar': '🇸🇦', 'hi': '🇮🇳', 'th': '🇹🇭', 'vi': '🇻🇳', 'tr': '🇹🇷',
      'pl': '🇵🇱', 'nl': '🇳🇱', 'sv': '🇸🇪', 'he': '🇮🇱'
    };
    
    // Replace the image with a text fallback
    const target = event.target;
    target.style.display = 'none';
    target.parentElement.innerHTML = `<div class="flag-emoji-fallback">${fallbackEmojis[langCode] || '🌐'}</div>` + target.parentElement.innerHTML.split('</img>')[1];
  }
}